import { useState, useEffect } from 'react';
import { connectUtil, PropsFromRedux } from 'base/src/utils/reduxUtil';
import { RootStateCampanha } from '../../../../store/campanhaReducer';
import { GetExtrato } from 'domain/store/_carteira/extrato/actions/extratoAction';
import ExtratoList from '../ExtratoList';
import NoData from 'base/src/components/NoData';
import StateMensage from 'base/src/view/states/state';
import { EXTRATO_RANGE } from 'domain/types/_carteira/extrato/extrato';
import { TextField, Box, MenuItem } from '@mui/material';
import { Button } from 'base/src/components/Button';
import { Icon } from '@iconify/react';
import { useTheme } from 'base/src/utils/context/themeContext';
import styled from 'styled-components';
import { CampanhasService } from '../../../../services/campanha/campanhasService';
import { CampanhaResponse } from 'campanha/src/types/campanha/campanhaResponse';
import { ResponseValid } from 'domain/utils/ReactUtils';
import { _UserData, UserDataBaseEnum } from 'base/src/infra/storage/localStorage';
import { exportToExcel } from 'domain/utils/excelUtil';
import { formatDateTime, formatDate } from 'base/src/utils/stringUtil';
import { formatToBRL } from 'base/src/utils/mathUtil';
import Drawer from 'base/src/components/Drawer';

const FilterBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  width: 100%;
`;

const RangeButtonGroup = styled.div`
  display: flex;
  gap: 4px;
  background-color: ${props => props.theme.colors.border_grey}20;
  border-radius: 8px;
  padding: 4px;
`;

const RangeButton = styled.button<{ $active: boolean }>`
  padding: 6px 14px;
  border: none;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 150ms ease;
  background-color: ${props => props.$active ? props.theme.colors.primary : 'transparent'};
  color: ${props => props.$active ? '#fff' : props.theme.colors.grey_text};

  &:hover {
    background-color: ${props => props.$active ? props.theme.colors.primary : props.theme.colors.hover};
  }
`;

const DrawerContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 10px;
  height: 100%;
`;

const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  h3 {
    margin: 0;
    font-size: 1.1rem;
    font-weight: 600;
    color: ${props => props.theme.colors.text};
  }
`;

const DrawerField = styled.div`
  width: 100%;
`;

const DrawerActions = styled.div`
  margin-top: auto;
  padding-top: 16px;
  border-top: 1px solid ${props => props.theme.colors.border_grey}30;
`;

const connector = connectUtil(
  (state: RootStateCampanha) => ({
    ExtratoRange: state.ExtratoReducer.Extrato,
    CarteiraUsuario: state.CarteiraBaseReducer.CarteiraUsuario,
  }),
  { GetExtrato }
);

export interface ExtratoTabProps extends PropsFromRedux<typeof connector> {}

const RANGE_OPTIONS = [
  { value: 'semana', label: 'Semana' },
  { value: 'mes', label: 'Mês' },
  { value: 'ano', label: 'Ano' },
];

function ExtratoTab(props: ExtratoTabProps) {
  const { theme } = useTheme();

  function toLocalDateString(date: Date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  function getDefaultStartDate() {
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    return toLocalDateString(weekAgo);
  }

  function getDefaultEndDate() {
    return toLocalDateString(new Date());
  }

  const [dateStart, setDateStart] = useState(getDefaultStartDate);
  const [dateEnd, setDateEnd] = useState(getDefaultEndDate);
  const [selectedRange, setSelectedRange] = useState('semana');
  const [campanhas, setCampanhas] = useState<CampanhaResponse[]>([]);
  const [selectedCampanhaId, setSelectedCampanhaId] = useState<number | undefined>(undefined);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  function calculateDateRange(range: string) {
    const today = new Date();
    const startDate = new Date();

    switch (range) {
      case 'semana':
        startDate.setDate(today.getDate() - 7);
        break;
      case 'mes':
        startDate.setMonth(today.getMonth() - 1);
        break;
      case 'ano':
        startDate.setFullYear(today.getFullYear() - 1);
        break;
      default:
        startDate.setDate(today.getDate() - 7);
    }

    return {
      start: toLocalDateString(startDate),
      end: toLocalDateString(today)
    };
  }

  function handleRangeClick(value: string) {
    setSelectedRange(value);
    const range = calculateDateRange(value);
    setDateStart(range.start);
    setDateEnd(range.end);
    fetchExtrato(range.start, range.end, selectedCampanhaId);
  }

  function fetchExtrato(start: string, end: string, campanhaId?: number) {
    if (!props.CarteiraUsuario?.length || props.CarteiraUsuario[0].CarteiraID === 0) return;
    const request = {
      From: start ? start + 'T00:00:00' : '',
      To: end ? end + 'T23:59:59' : '',
      CampanhaId: campanhaId,
    };
    props.GetExtrato(request, props.CarteiraUsuario[0].CarteiraID, function noop() {});
  }

  function handleApplyDateFilter() {
    setSelectedRange('');
    fetchExtrato(dateStart, dateEnd, selectedCampanhaId);
    setIsDrawerOpen(false);
  }

  function handleExportExcel() {
    if (!props.ExtratoRange?.length) return;
    exportToExcel(props.ExtratoRange, [
      { header: 'ID', accessor: function getId(item) { return item.Id; } },
      { header: 'Data', accessor: function getData(item) { return formatDateTime(item.DataOperacao) || ''; } },
      { header: 'Descrição', accessor: function getDescricao(item) { return item.Descricao; } },
      { header: 'Tipo', accessor: function getTipo(item) { return item.Tipo; } },
      { header: 'Status', accessor: function getStatus(item) { return item.Status; } },
      { header: 'Valor', accessor: function getValor(item) { return formatToBRL(item.ValorAdicionado) || ''; } },
      { header: 'Balanço', accessor: function getBalanco(item) { return formatToBRL(item.NovoBalanco) || ''; } },
    ], 'extrato-campanha');
  }

  function handleCampanhaChange(campanhaId: number | undefined) {
    setSelectedCampanhaId(campanhaId);
  }

  useEffect(function fetchCampanhas() {
    const usuarioId = Number(_UserData.Get(UserDataBaseEnum.UsuarioID));
    const empresaId = Number(_UserData.Get(UserDataBaseEnum.EmpresaID));

    if (!usuarioId || !empresaId) return;

    const service = new CampanhasService();
    service.GetAtivosByUsuarioPagador(usuarioId, empresaId).then(function handleCampanhasResponse(response) {
      if (ResponseValid(response, false)) {
        setCampanhas(response as CampanhaResponse[]);
      }
    });
  }, []);

  useEffect(function fetchInitialExtrato() {
    fetchExtrato(dateStart, dateEnd, selectedCampanhaId);
  }, [props.CarteiraUsuario]);

  return (
    <>
      <StateMensage stateLoadingNames={[EXTRATO_RANGE]} />

      <FilterBar>
        <RangeButtonGroup>
          {RANGE_OPTIONS.map(function renderRangeOption(option) {
            return (
              <RangeButton
                key={option.value}
                $active={selectedRange === option.value}
                onClick={function handleClick() { handleRangeClick(option.value); }}
              >
                {option.label}
              </RangeButton>
            );
          })}
        </RangeButtonGroup>

        <Button
          size="sm"
          variant="primary"
          onClick={function openDrawer() { setIsDrawerOpen(true); }}
          style={{ gap: '6px', marginLeft: 'auto' }}
        >
          <Icon icon="ph:funnel-bold" height={16} />
          Filtros
        </Button>
        <Button
          size="sm"
          variant="secondary"
          onClick={handleExportExcel}
          style={{ gap: '6px' }}
        >
          <Icon icon="ph:file-xls-bold" height={16} />
          Exportar Excel
        </Button>
      </FilterBar>

      <Drawer isOpen={isDrawerOpen} onClose={function closeDrawer() { setIsDrawerOpen(false); }} width="380px">
        <DrawerContent>
          <DrawerHeader>
            <h3>Filtros</h3>
            <Button size="xsm" variant="light" onClick={function closeDrawer() { setIsDrawerOpen(false); }}>
              <Icon icon="ph:x-bold" height={18} />
            </Button>
          </DrawerHeader>

          <DrawerField>
            <TextField
              label="Data Inicial"
              type="date"
              size="small"
              fullWidth
              value={dateStart}
              onChange={function handleStartChange(e) { setDateStart(e.target.value); }}
              slotProps={{ inputLabel: { shrink: true } }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: theme.colors.card,
                  '&:hover fieldset': { borderColor: theme.colors.primary },
                },
              }}
            />
          </DrawerField>

          <DrawerField>
            <TextField
              label="Data Final"
              type="date"
              size="small"
              fullWidth
              value={dateEnd}
              onChange={function handleEndChange(e) { setDateEnd(e.target.value); }}
              slotProps={{ inputLabel: { shrink: true } }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: theme.colors.card,
                  '&:hover fieldset': { borderColor: theme.colors.primary },
                },
              }}
            />
          </DrawerField>

          <DrawerField>
            <TextField
              label="Campanha"
              select
              size="small"
              fullWidth
              value={selectedCampanhaId ?? ''}
              onChange={function onCampanhaChange(e) {
                handleCampanhaChange(e.target.value ? Number(e.target.value) : undefined);
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: theme.colors.card,
                  '&:hover fieldset': { borderColor: theme.colors.primary },
                },
              }}
            >
              <MenuItem value="">Todas as campanhas</MenuItem>
              {campanhas.map(function renderCampanhaOption(campanha) {
                return (
                  <MenuItem key={campanha.CampanhaID} value={campanha.CampanhaID}>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <span>{campanha.Titulo}</span>
                      <span style={{ fontSize: '0.75rem', color: '#9e9e9e' }}>
                        ID: {campanha.CampanhaID} | {formatDate(campanha.DataInicio)} - {formatDate(campanha.DataTermino)}
                      </span>
                    </Box>
                  </MenuItem>
                );
              })}
            </TextField>
          </DrawerField>

          <DrawerActions>
            <Button
              size="sm"
              variant="primary"
              full
              onClick={handleApplyDateFilter}
              style={{ gap: '6px' }}
            >
              <Icon icon="ph:funnel-bold" height={16} />
              Aplicar Filtros
            </Button>
          </DrawerActions>
        </DrawerContent>
      </Drawer>

      {props.ExtratoRange === undefined || props.ExtratoRange.length < 1
        ? <NoData />
        : <ExtratoList extrato={props.ExtratoRange} hideValues={false} />
      }
    </>
  );
}

export default connector(ExtratoTab);
